import type { PostSource } from "../types";
import type { ApiWindow, EffectiveStatusValue } from "./apiTypes";

/**
 * Glance tiers (SPEC §19.3, R-606..R-611): a time-aware traffic-light view of an area.
 *   off     red     power is off now (ongoing), or a possible/scheduled window covers now
 *   soon    orange  power on, an outage window starts within SOON_THRESHOLD_MS
 *   later   yellow  power on, an outage window starts later than that
 *   clear   green   power on, nothing announced
 *   unknown grey    the advisory could not be read
 */
export type GlanceTier = "off" | "soon" | "later" | "clear" | "unknown";

export const GLANCE_TIERS: readonly GlanceTier[] = ["off", "soon", "later", "clear", "unknown"] as const;

export const SOON_THRESHOLD_MS = 3 * 60 * 60 * 1000;

export interface GlanceMetadata {
  value: GlanceTier;
  label: string;
  sublabel: string;
  color: string;
  stroke: string;
  cue: string;
}

export const GLANCE_METADATA: Record<GlanceTier, GlanceMetadata> = {
  off: {
    value: "off",
    label: "Outage now",
    sublabel: "Power is off, or may be off",
    color: "#E5484D",
    stroke: "#9F1239",
    cue: "thick outline",
  },
  soon: {
    value: "soon",
    label: "Outage soon",
    sublabel: "Starts within 3 hours",
    color: "#F97316",
    stroke: "#9A3412",
    cue: "solid outline",
  },
  later: {
    value: "later",
    label: "Outage later",
    sublabel: "Power on now, a window is planned",
    color: "#EAB308",
    stroke: "#854D0E",
    cue: "dashed outline",
  },
  clear: {
    value: "clear",
    label: "No outage planned",
    sublabel: "Power on, nothing announced",
    color: "#22C55E",
    stroke: "#166534",
    cue: "thin outline",
  },
  unknown: {
    value: "unknown",
    label: "Unknown",
    sublabel: "Advisory could not be read",
    color: "#64748B",
    stroke: "#334155",
    cue: "dotted outline",
  },
};

export function isGlanceTier(value: unknown): value is GlanceTier {
  return typeof value === "string" && GLANCE_TIERS.includes(value as GlanceTier);
}

/**
 * Filter conditions (R-612): the five display tiers plus two tags an area can carry in addition to
 * its tier. An area is shown when ANY of its conditions is checked; it is painted with its tier.
 */
export type GlanceCondition = GlanceTier | "restored" | "cancelled";

export const GLANCE_CONDITIONS: readonly GlanceCondition[] = [...GLANCE_TIERS, "restored", "cancelled"] as const;

export interface ConditionMetadata {
  value: GlanceCondition;
  label: string;
  sublabel: string;
  cue: string;
}

export const CONDITION_METADATA: Record<GlanceCondition, ConditionMetadata> = {
  ...GLANCE_METADATA,
  restored: {
    value: "restored",
    label: "Restored today",
    sublabel: "VECO said power is back today",
    cue: "check tag",
  },
  cancelled: {
    value: "cancelled",
    label: "Cancelled",
    sublabel: "An announced interruption was called off",
    cue: "strike tag",
  },
};

export function isGlanceCondition(value: unknown): value is GlanceCondition {
  return typeof value === "string" && GLANCE_CONDITIONS.includes(value as GlanceCondition);
}

export interface GlanceSource {
  status: EffectiveStatusValue;
  stale?: boolean;
  lastKnownStatus?: EffectiveStatusValue;
  activeWindow?: ApiWindow | null;
  nextWindow?: ApiWindow | null;
}

export interface GlanceCountdown {
  kind: "starts_in" | "ends_in";
  target: string;
}

export interface Glance {
  tier: GlanceTier;
  /** Tier label, prefixed with "Possible" when the driving status is `possible`. */
  label: string;
  /** True when the driving status is `possible` (unconfirmed rotational window). */
  possible: boolean;
  /** Live countdown target for the driving window, or null when nothing is pending. */
  countdown: GlanceCountdown | null;
  /** The window that decides the tier: the current one for `off`, the next one for `soon`/`later`. */
  window: ApiWindow | null;
  /** Every condition the area satisfies: its tier plus `restored` / `cancelled` tags from the raw status. */
  conditions: GlanceCondition[];
}

export function effectiveStatus(source: GlanceSource): EffectiveStatusValue {
  return source.stale && source.lastKnownStatus ? source.lastKnownStatus : source.status;
}

function windowMs(window: ApiWindow | null | undefined): { start: number; end: number } | null {
  if (!window) {
    return null;
  }
  const start = new Date(window.start).getTime();
  const end = new Date(window.end).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return null;
  }
  return { start, end };
}

export function windowCoversNow(window: ApiWindow | null | undefined, now: Date): boolean {
  const range = windowMs(window);
  if (!range) {
    return false;
  }
  const time = now.getTime();
  return range.start <= time && time < range.end;
}

/** The earliest window (active or next) whose start is still in the future, or null. */
export function nextUpcomingWindow(source: GlanceSource, now: Date): ApiWindow | null {
  const time = now.getTime();
  let best: ApiWindow | null = null;
  let bestStart = Number.POSITIVE_INFINITY;
  for (const candidate of [source.activeWindow, source.nextWindow]) {
    const range = windowMs(candidate);
    if (candidate && range && range.start > time && range.start < bestStart) {
      best = candidate;
      bestStart = range.start;
    }
  }
  return best;
}

function tierLabel(tier: GlanceTier, possible: boolean): string {
  const base = GLANCE_METADATA[tier].label;
  if (possible && (tier === "off" || tier === "soon" || tier === "later")) {
    return `Possible ${base.charAt(0).toLowerCase()}${base.slice(1)}`;
  }
  return base;
}

function withConditions(status: EffectiveStatusValue, glance: Omit<Glance, "conditions">): Glance {
  const conditions: GlanceCondition[] = [glance.tier];
  if (status === "restored") {
    conditions.push("restored");
  }
  if (status === "cancelled") {
    conditions.push("cancelled");
  }
  return { ...glance, conditions };
}

/**
 * Display hierarchy (R-607): off > soon > later > unknown > clear. A future window always outranks a
 * past restoration or cancellation, which survive only as conditions/tags.
 */
export function computeGlance(source: GlanceSource, now: Date = new Date()): Glance {
  const status = effectiveStatus(source);
  const possible = status === "possible";

  if (status === "unknown") {
    return withConditions(status, {
      tier: "unknown",
      label: tierLabel("unknown", false),
      possible: false,
      countdown: null,
      window: null,
    });
  }

  if (status === "ongoing") {
    const active = source.activeWindow ?? null;
    const range = windowMs(active);
    const countdown: GlanceCountdown | null =
      active && range && range.end > now.getTime() ? { kind: "ends_in", target: active.end } : null;
    return withConditions(status, { tier: "off", label: tierLabel("off", false), possible: false, countdown, window: active });
  }

  if (status === "possible" || status === "scheduled") {
    // Between polls a `nextWindow` can start before the server promotes it to `activeWindow`.
    const covering = [source.activeWindow, source.nextWindow].find((window) => windowCoversNow(window, now)) ?? null;
    if (covering) {
      return withConditions(status, {
        tier: "off",
        label: tierLabel("off", possible),
        possible,
        countdown: { kind: "ends_in", target: covering.end },
        window: covering,
      });
    }
  }

  const next = nextUpcomingWindow(source, now);
  if (next) {
    const startsInMs = new Date(next.start).getTime() - now.getTime();
    const tier: GlanceTier = startsInMs <= SOON_THRESHOLD_MS ? "soon" : "later";
    return withConditions(status, {
      tier,
      label: tierLabel(tier, possible),
      possible,
      countdown: { kind: "starts_in", target: next.start },
      window: next,
    });
  }

  return withConditions(status, {
    tier: "clear",
    label: tierLabel("clear", false),
    possible: false,
    countdown: null,
    window: null,
  });
}

/** An area is shown when any of its conditions is in the checked set (R-612). */
export function matchesFilters(glance: Pick<Glance, "conditions">, filters: ReadonlySet<GlanceCondition>): boolean {
  return glance.conditions.some((condition) => filters.has(condition));
}

export function emptyConditionCounts(): Record<GlanceCondition, number> {
  return { off: 0, soon: 0, later: 0, clear: 0, unknown: 0, restored: 0, cancelled: 0 };
}

/** Counts per condition; an area with several conditions is counted under each of them. */
export function conditionCounts(sources: readonly GlanceSource[], now: Date = new Date()): Record<GlanceCondition, number> {
  const counts = emptyConditionCounts();
  for (const source of sources) {
    for (const condition of computeGlance(source, now).conditions) {
      counts[condition] += 1;
    }
  }
  return counts;
}

/** Sort order for directories and lists: most urgent first. */
export const GLANCE_ORDER: Record<GlanceTier, number> = {
  off: 0,
  soon: 1,
  later: 2,
  unknown: 3,
  clear: 4,
};

/** R-639: accuracy note under the area hero; portal-driven areas name the calendar. */
export function glanceAccuracyNote(source: PostSource | null | undefined): string {
  return source === "portal_sheet"
    ? "Based on VECO's calendar. Actual switching can run early or late."
    : "Based on VECO's announcement. Actual switching can run early or late.";
}
